import { useEffect, useRef, type Dispatch, type RefObject, type SetStateAction } from 'react';
import { Raycaster, Vector2, Vector3, type Camera, type Scene, type WebGLRenderer } from 'three';

import { matchesInputAction } from 'app/modules/wallforming/components/input-binding/hooks/useInputActionHandler';
import { InputActionId } from 'app/modules/wallforming/components/input-binding/types/inputActionId';
import { InputTrigger } from 'app/modules/wallforming/components/input-binding/types/inputTrigger';
import { chordFromMouseEvent } from 'app/modules/wallforming/components/input-binding/utils/inputChordUtils';
import { isPoiIntersection } from 'app/modules/wallforming/components/scenes/objects/utils/sceneUtils';
import {
    getClickedSnapTarget,
    getEdgeKey,
    getNearestPanel,
    getPanelDimension,
    getPartAxisScale,
    getPointerAxisDistance,
    getSelectedPanel,
    getSnapGridAxes,
    isRotation90Degrees,
    PANEL_REPLACEMENT_SNAP_HYSTERESIS,
    replacePanel
} from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/utils/quickPanelReplacementUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { Part } from 'app/modules/wallforming/components/scenes/objects/articles/types/part';
import type {
    EdgeHandle,
    MousePreview,
    PanelGridItem,
    PanelReplacementDirection,
    PanelReplacementGrid,
    SelectedPanel
} from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/types/quickPanelReplacementTypes';

interface ReplacementPointerEventHandlerProps {
    camera: Camera;
    renderer: WebGLRenderer;
    scene: Scene;
    isOpen: boolean;
    selected: SelectedPanel | null;
    anchorPart?: Part;
    edgeHandles: EdgeHandle[];
    grid: PanelReplacementGrid;
    snapCandidates: PanelGridItem[];
    currentScreenDirections: Record<string, PanelReplacementDirection>;
    mouseEdgeRef: RefObject<EdgeHandle | null>;
    previewRef: RefObject<MousePreview | null>;
    canvasRef: RefObject<HTMLCanvasElement | null>;
    initialCursorRef: RefObject<string>;
    setActiveEdge: Dispatch<SetStateAction<EdgeHandle | null>>;
    setMousePreview: Dispatch<SetStateAction<MousePreview | null>>;
}

export const useReplacementPointerEventHandler = ({
    camera,
    renderer,
    scene,
    isOpen,
    selected,
    anchorPart,
    edgeHandles,
    grid,
    snapCandidates,
    currentScreenDirections,
    mouseEdgeRef,
    previewRef,
    canvasRef,
    initialCursorRef,
    setActiveEdge,
    setMousePreview
}: ReplacementPointerEventHandlerProps) => {
    const selectionToken = selected ? `${selected.articleId}:${selected.instanceId}` : undefined;
    const activeSelectionRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (!isOpen || !selected || activeSelectionRef.current !== selectionToken) {
            mouseEdgeRef.current = null;
            previewRef.current = null;
            setActiveEdge(null);
            setMousePreview(null);
        }
        activeSelectionRef.current = selectionToken;
        if (!isOpen || !selected) {
            return;
        }

        const canvas = renderer.domElement;
        const initialCursor = canvas.style.cursor;
        canvasRef.current = canvas;
        initialCursorRef.current = initialCursor;

        const clearPreview = () => {
            if (previewRef.current !== null) {
                previewRef.current = null;
                setMousePreview(null);
            }
        };

        const updatePreview = (event: PointerEvent): MousePreview | null => {
            const edge = mouseEdgeRef.current;
            const currentSelection = getSelectedPanel(usePlacementStore.getState(), grid);
            if (!edge || !currentSelection) {
                clearPreview();
                return null;
            }

            const direction = currentScreenDirections[getEdgeKey(edge)];
            const { origin, gridAxis } = getSnapGridAxes(edge, direction, camera);
            const pointerDistance = getPointerAxisDistance(event, canvas, camera, origin, gridAxis);
            if (pointerDistance === undefined) {
                clearPreview();
                return null;
            }

            const rotated90 = isRotation90Degrees(anchorPart?.rotation ?? 0);
            const scale = getPartAxisScale(anchorPart?.scale ?? new Vector3(1, 1, 1), edge.localAxis);
            const currentSize = getPanelDimension(currentSelection.item, edge.dimension, rotated90);
            const candidates = [currentSelection.item, ...snapCandidates];
            const requestedSize = currentSize + pointerDistance / scale;
            const nearestTarget = getNearestPanel(
                grid,
                currentSelection.item,
                edge.dimension,
                requestedSize,
                rotated90,
                Object.keys(currentSelection.instance.parts).length
            );

            const previousTarget = previewRef.current?.target;
            const target =
                nearestTarget &&
                previousTarget &&
                candidates.some((candidate) => candidate.article.id === previousTarget.article.id) &&
                Math.abs(getPanelDimension(previousTarget, edge.dimension, rotated90) - requestedSize) <=
                    Math.abs(getPanelDimension(nearestTarget, edge.dimension, rotated90) - requestedSize) +
                        PANEL_REPLACEMENT_SNAP_HYSTERESIS
                    ? previousTarget
                    : nearestTarget;
            if (!target) {
                clearPreview();
                return null;
            }

            const preview = {
                edge,
                target,
                selectionToken: `${currentSelection.articleId}:${currentSelection.instanceId}`
            };
            previewRef.current = preview;
            setMousePreview(preview);
            return preview;
        };

        const handlePointerMove = (event: PointerEvent) => {
            updatePreview(event);
            const edge = mouseEdgeRef.current;
            if (edge && anchorPart && event.target === canvas) {
                const target = getClickedSnapTarget(
                    event,
                    canvas,
                    camera,
                    selected.item,
                    snapCandidates,
                    edge,
                    isRotation90Degrees(anchorPart.rotation),
                    getPartAxisScale(anchorPart.scale, edge.localAxis),
                    currentScreenDirections[getEdgeKey(edge)]
                );
                canvas.style.cursor = target ? 'pointer' : 'crosshair';
            }
        };

        const handlePointerDown = (event: PointerEvent) => {
            if (event.button !== 0 || event.target !== canvas || !mouseEdgeRef.current) {
                return;
            }
            const currentSelection = getSelectedPanel(usePlacementStore.getState(), grid);
            const edge = mouseEdgeRef.current;
            const part = currentSelection?.instance.parts[Object.keys(currentSelection.instance.parts)[0]];

            const clickedSnapTarget =
                currentSelection &&
                part &&
                getClickedSnapTarget(
                    event,
                    canvas,
                    camera,
                    currentSelection.item,
                    snapCandidates,
                    edge,
                    isRotation90Degrees(part.rotation),
                    getPartAxisScale(part.scale, edge.localAxis),
                    currentScreenDirections[getEdgeKey(edge)]
                );

            if (!clickedSnapTarget) {
                const chord = chordFromMouseEvent(event, InputTrigger.Click);
                const doubleClickChord = chordFromMouseEvent(event, InputTrigger.DoubleClick);
                const canSelectPoi = [InputActionId.PoiSelect, InputActionId.PoiSelectAndAlign].some(
                    (action) => matchesInputAction(action, chord) || matchesInputAction(action, doubleClickChord)
                );
                if (canSelectPoi) {
                    const rect = canvas.getBoundingClientRect();
                    const pointer = new Vector2(
                        ((event.clientX - rect.left) / rect.width) * 2 - 1,
                        1 - ((event.clientY - rect.top) / rect.height) * 2
                    );
                    const raycaster = new Raycaster();
                    raycaster.layers.enableAll();
                    raycaster.setFromCamera(pointer, camera);
                    const poiHits = raycaster.intersectObjects(
                        scene.getObjectsByProperty('name', 'poi-interaction-target')
                    );
                    if (poiHits.some(isPoiIntersection)) {
                        return;
                    }
                }
            }
            event.preventDefault();
            event.stopImmediatePropagation();

            const preview = clickedSnapTarget ? { edge, target: clickedSnapTarget } : updatePreview(event);
            if (preview && currentSelection && preview.target.article.id !== currentSelection.articleId) {
                replacePanel(usePlacementStore.getState(), currentSelection, preview.target, preview.edge);
            }

            mouseEdgeRef.current = null;
            previewRef.current = null;
            setActiveEdge(null);
            setMousePreview(null);
            canvas.style.cursor = initialCursor;
        };

        window.addEventListener('pointermove', handlePointerMove);
        canvas.addEventListener('pointerdown', handlePointerDown, true);
        return () => {
            window.removeEventListener('pointermove', handlePointerMove);
            canvas.removeEventListener('pointerdown', handlePointerDown, true);
            canvas.style.cursor = initialCursor;
            canvasRef.current = null;
        };
    }, [
        anchorPart,
        camera,
        canvasRef,
        currentScreenDirections,
        edgeHandles,
        grid,
        initialCursorRef,
        isOpen,
        selectionToken,
        mouseEdgeRef,
        previewRef,
        renderer,
        scene,
        selected,
        setActiveEdge,
        setMousePreview,
        snapCandidates
    ]);
};
